import React, { useRef } from 'react'
import * as yup from 'yup'

import { Formik, FormikProps } from 'formik'
import {
  WorkspaceMinimalFragment,
  WorkspaceMembershipFragment,
  ProjectMembershipFragment,
} from '@sketch/gql-types'
import {
  useFlag,
  InvitePeopleField,
  useUserProfile,
} from '@sketch/modules-common'
import { AddProjectMemberPermissionsAccessSelect } from 'modules/projects/components/ProjectMemberPermissionsAccessSelect/ProjectMemberPermissionsAccessSelect'
import {
  FormValues,
  useAutocomplete,
} from '../AddNewProjectModal/useAutocomplete'
import {
  InviteButton,
  MemberInviteSection,
  NonMemberError,
} from './ProjectSharingModal.styles'

// Form Schema
const FORM_INITIAL_VALUES: FormValues = {
  email: '',
  workspaceMembershipIdentifier: '',
  shouldValidateEmail: true,
  guestCommentsEnabled: true,
  guestInspectEnabled: true,
}

const memberFormSchema = yup.object().shape({
  shouldValidateEmail: yup.boolean(),
  email: yup.string().max(255).when('shouldValidateEmail', {
    is: true,
    then: yup.string().trim().required().lowercase().email(),
  }),
  workspaceMembershipIdentifier: yup.string().trim().nullable(),
  guestCommentsEnabled: yup.boolean(),
  guestInspectEnabled: yup.boolean(),
})

interface FormikRenderProps {
  formikBag: FormikProps<typeof FORM_INITIAL_VALUES>
  projectIdentifier: string
  workspaceIdentifier: string
  membersList: WorkspaceMembershipFragment[] | ProjectMembershipFragment[]
  hideLabel?: boolean
  filterMembers?: 'Guests' | 'Members'
  removeSelf?: boolean
}

interface AddMemberFormProps {
  workspace: WorkspaceMinimalFragment
  membersList: WorkspaceMembershipFragment[] | ProjectMembershipFragment[]
  onMemberAdd: (values: FormValues) => void
  projectIdentifier: string
  hideLabel?: boolean
  filterMembers?: 'Guests' | 'Members'
  removeSelf?: boolean
}

export const AddMemberForm = ({
  workspace,
  projectIdentifier,
  membersList,
  onMemberAdd,
  hideLabel,
  filterMembers,
  removeSelf = false,
}: AddMemberFormProps) => {
  const { data: userData } = useUserProfile()

  const handleValidateEmail = ({ email }: { email: string }) => {
    const errors: Partial<Record<keyof FormValues, string>> = {}

    if (filterMembers === undefined || !email) {
      return
    }

    // this extra condition needs to be here for cases like ines@omg.l, which is still not a valid email
    // but no worth checking against the member emails yet
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
      errors.email = 'Invalid email'

      return errors
    }

    if (removeSelf && email === userData?.me.email) {
      errors.email = 'Cannot invite yourself'
    }

    // check if email is among the ones on the workspace
    const isMember = membersList?.some(
      member => 'user' in member && member.user?.email === email
    )

    if (!isMember && filterMembers === 'Members') {
      errors.email = 'Is not an inviteable member of this workspace'
    } else if (isMember && filterMembers === 'Guests') {
      errors.email =
        'Is a member of this workspace and cannot be invited as a Guest'
    }

    return errors
  }

  return (
    <Formik
      initialValues={FORM_INITIAL_VALUES}
      onSubmit={async (values, { resetForm }) => {
        await onMemberAdd(values)
        resetForm()
      }}
      validateOnChange={true}
      validateOnMount={true}
      validationSchema={memberFormSchema}
      validate={handleValidateEmail}
    >
      {formikBag => (
        <form
          onSubmit={e => {
            e.preventDefault()
          }}
        >
          <FormikRender
            formikBag={formikBag}
            membersList={membersList}
            projectIdentifier={projectIdentifier}
            workspaceIdentifier={workspace.identifier}
            hideLabel={hideLabel}
            filterMembers={filterMembers}
            removeSelf={removeSelf}
          />
        </form>
      )}
    </Formik>
  )
}

const FormikRender = ({
  formikBag,
  workspaceIdentifier,
  projectIdentifier,
  membersList,
  hideLabel,
  filterMembers,
  removeSelf,
}: FormikRenderProps) => {
  const isGuestsInProjectEnabled = useFlag('guests-in-project')
  const containerRef = useRef<HTMLInputElement>(null)
  const { data: userData } = useUserProfile()
  const autocomplete = useAutocomplete({
    projectIdentifier,
    workspaceIdentifier,
    membersList: (membersList as WorkspaceMembershipFragment[])
      .filter(member => Boolean(member.identifier))
      .map(member => member.identifier) as string[],
    options: {
      userIdentifier: removeSelf ? userData?.me.identifier : undefined,
      skipWorkspaceMembers: filterMembers === 'Guests',
    },
  })

  const showError =
    filterMembers &&
    [
      'Cannot invite yourself',
      'Is not an inviteable member of this workspace',
      'Is a member of this workspace and cannot be invited as a Guest',
    ].includes(formikBag.errors.email ?? '')

  return (
    <>
      <MemberInviteSection>
        <InvitePeopleField
          containerRef={containerRef}
          autocomplete={autocomplete}
          placeholder="Add a name or email address"
          label={hideLabel ? '' : 'Manage Access'}
          dynamicAction={props => {
            if (!isGuestsInProjectEnabled) {
              return
            }

            const workspaceMembership = props.selectedItem as Partial<WorkspaceMembershipFragment>
            const membership = {
              workspaceMembership: workspaceMembership ?? {
                __typename: 'WorkspaceMembership',
                role: 'GUEST',
              },
              privateAccessLevel: workspaceMembership?.isEditor
                ? ('EDIT' as ProjectMembershipFragment['privateAccessLevel'])
                : ('VIEW' as ProjectMembershipFragment['privateAccessLevel']),
            }

            return (
              <AddProjectMemberPermissionsAccessSelect
                member={membership}
                guestCommentsEnabled={
                  autocomplete.formik.values.guestCommentsEnabled
                }
                guestInspectEnabled={
                  autocomplete.formik.values.guestInspectEnabled
                }
                onPermissionChange={(permission, value) => {
                  autocomplete.formik.setFieldValue(permission, value)
                }}
                onlyMembers={filterMembers === 'Members'}
              />
            )
          }}
        />
        <InviteButton
          type="submit"
          name="Add member to project"
          disabled={!autocomplete.formik.isValid}
          onClick={async () => {
            // Form is submitted here instead of <form />
            // because we need to reset the Combobox, and
            // the autocomplete is created in this component
            await formikBag.submitForm()
            autocomplete.combobox.reset()
          }}
        >
          Add
        </InviteButton>
      </MemberInviteSection>
      {showError && <NonMemberError>{formikBag.errors.email}</NonMemberError>}
    </>
  )
}
